import styled, { css } from 'styled-components';

import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurShadow,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';

export const CompareProductContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: ${getPurpurSpacing('100')} 0;

    ${purpurMedia('md')`
        padding: ${getPurpurSpacing('100')} ${getPurpurSpacing('200')};
    `};

    ${purpurMedia('lg')`
        border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray300')};
        border-radius: ${getPurpurBorder('radiusMd')};
        padding: ${getPurpurSpacing('200')};
        min-width: 19rem;
    `};

    &:focus-within {
        outline: 2px solid ${getPurpurColor('purple500')};
        outline-offset: 2px;
    }
    &:focus-within:not(:has(:focus-visible)) {
        outline: 0;
    }

    &:hover {
        box-shadow: ${getPurpurShadow('lg')};
    }
`;

export const CardHeader = styled.div``;

export const ImageContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;

    img {
        object-fit: contain;
    }
`;

export const ProductName = styled.div`
    color: ${getPurpurColor('brandOffBlack')};
    font-weight: ${getPurpurTypography('weightBold')};
    word-break: break-word;
`;

type ProductCardVariantProps = {
    $variant: 'full' | 'minimal';
};

const minimalCardHeaderStyle = css`
    margin-bottom: ${getPurpurSpacing('100')};

    ${CardHeader} {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${getPurpurSpacing('100')};
    }

    ${ImageContainer} {
        width: 2.5rem;
        height: 2.5rem;
        flex-shrink: 0;
    }

    ${ProductName} {
        flex: 1;

        font-size: ${getPurpurTypography('scale75')};
        ${purpurMedia('lg')`
            font-size: ${getPurpurTypography('scale100')};
        `};
    }
`;

const fullCardHeaderStyle = css`
    ${minimalCardHeaderStyle};

    ${CardHeader} {
        margin-bottom: ${getPurpurSpacing('100')};
    }

    ${ProductName} {
        font-size: ${getPurpurTypography('scale75')};
    }

    ${purpurMedia('lg')`
        margin-bottom: ${getPurpurSpacing('200')};
        ${CardHeader} {
            display: block;
            margin-bottom: ${getPurpurSpacing('200')};
        }
        ${ImageContainer} {
            width: auto;
            height: 11.25rem;
            margin-bottom: ${getPurpurSpacing('200')};
            line-height: ${getPurpurTypography('lineHeightDefault')};
        }
        ${ProductName} {
            font-size: ${getPurpurTypography('scale200')};
            line-height: ${getPurpurTypography('lineHeightLoose')};
            height: 3.75rem;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }
    `};
`;

export const ProductLink = styled.a<ProductCardVariantProps>`
    flex: 1;
    display: block;
    text-decoration: none;

    ${({ $variant }) => ($variant === 'minimal' ? minimalCardHeaderStyle : fullCardHeaderStyle)};

    &:active,
    &:hover,
    &:focus {
        text-decoration: none;
        outline: 0;
    }
`;

export const MarketingTag = styled.img`
    display: none;

    ${purpurMedia('lg')`
        display: block;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 0;
        width: auto;
        height: auto;
        pointer-events: none;
        max-width: 5rem;
        max-height: 5rem;
    `};
`;

export const StockStatus = styled.div`
    display: none;

    ${purpurMedia('lg')`
        display: block;
        font-size: ${getPurpurTypography('scale50')};
        color: ${getPurpurColor('textStatusErrorMedium')};
        height: 1.125rem;
    `};
`;

export const PriceBlock = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    line-height: ${getPurpurTypography('lineHeightTight')};

    ${purpurMedia('lg')`
        line-height: ${getPurpurTypography('lineHeightLoose')};
    `};
`;

export const MonthlyPriceBlock = styled.div`
    display: flex;
    align-items: baseline;
    flex-direction: column;
`;

export const LeasingInitialAmountBlock = styled.div`
    display: flex;
    flex-direction: column;
`;

type FullPriceBlockProps = {
    $hasMonthlyPrice: boolean;
};

export const FullPriceBlock = styled.div<FullPriceBlockProps>`
    ${({ $hasMonthlyPrice }) => css`
        display: ${$hasMonthlyPrice ? 'none' : 'block'};
        ${purpurMedia('lg')`
            padding-left: ${$hasMonthlyPrice ? getPurpurSpacing('200') : 0};
            margin-left: ${$hasMonthlyPrice ? getPurpurSpacing('200') : 0};
            display: flex;
            flex-direction: column;
        `};
    `};
`;

export const GrayText = styled.span`
    font-size: ${getPurpurTypography('scale50')};
    color: ${getPurpurColor('beige700')};
`;

export const MonthlyPriceLabelText = styled.span`
    color: ${getPurpurColor('brandOffBlack')};
    font-size: ${getPurpurTypography('scale50')};
    margin-right: ${getPurpurSpacing('50')};

    ${purpurMedia('lg')`
        font-size: ${getPurpurTypography('scale75')};
    `};
`;

export const PriceText = styled.span`
    font-size: ${getPurpurTypography('scale100')};
    font-weight: ${getPurpurTypography('weightBold')};
    color: ${getPurpurColor('brandOffBlack')};

    ${purpurMedia('lg')`
        font-size: ${getPurpurTypography('scale200')};
    `};
`;

export const CurrencyText = styled.span`
    color: ${getPurpurColor('brandOffBlack')};
    font-size: ${getPurpurTypography('scale50')};
    margin-left: ${getPurpurSpacing('50')};

    ${purpurMedia('lg')`
        font-size: ${getPurpurTypography('scale75')};
    `};
`;

export const CurrencyTextBold = styled(CurrencyText)`
    font-weight: ${getPurpurTypography('weightBold')};
`;

export const OldPrice = styled.div`
    font-size: ${getPurpurTypography('scale50')};
    color: ${getPurpurColor('textDefault')};
    text-decoration: line-through;
    text-align: right;
`;

export const CompareActionsContainerDesktop = styled.div`
    display: none;
    flex-shrink: 0;

    ${purpurMedia('lg')`
        display: flex;
        justify-content: space-between;
    `};
`;

export const CompareActionsContainerMobile = styled.div`
    display: flex;
    justify-content: space-between;
    flex-shrink: 0;

    ${purpurMedia('lg')`
        display: none;
    `};
`;
